import { DraggableProvided, DraggableRubric, DraggableStateSnapshot } from "@hello-pangea/dnd";

import { cn } from "@/lib/utils";
import { IMessageStore } from "@/components/Playground/Messages/Message/message-store";
import { RenderItemFn } from "@/components/Playground/Messages/PlaceholderContainer/dnd/types";
import { getVerticalOnlyStyle } from "@/components/Playground/Messages/PlaceholderContainer/dnd/utils";

interface DraggableItemProps {
  innerRef: DraggableProvided["innerRef"];
  draggableProps: DraggableProvided["draggableProps"];
  dragHandleProps: DraggableProvided["dragHandleProps"];
  isDragging: boolean;
  isClone: boolean;
  message: IMessageStore;
  index: number;
  renderItem: RenderItemFn;
}

/**
 * Wrapper component for draggable items in the nested DnD container.
 *
 * This component is used in TWO contexts during a drag operation:
 *
 * 1. **Original item** (`isClone={false}`):
 *    - Stays in its position in the list
 *    - Does NOT spread dragHandleProps on the wrapper (handle is inside via renderItem)
 *
 * 2. **Clone item** (`isClone={true}`):
 *    - Rendered at document.body level via the `renderClone` prop
 *    - Follows the cursor during drag
 *
 * @see createRenderDraggableItemClone - Factory that creates the clone renderer
 */
export const DraggableItem = ({
  innerRef,
  draggableProps,
  dragHandleProps,
  isDragging,
  isClone,
  message,
  index,
  renderItem,
}: DraggableItemProps) => (
  <div
    ref={innerRef}
    {...draggableProps}
    {...(isClone ? dragHandleProps : {})}
    style={isClone ? getVerticalOnlyStyle(draggableProps.style) : draggableProps.style}
    className={cn(
      "relative rounded-lg border border-gray-200 bg-white",
      "transition-all duration-200",
      isDragging && isClone && "shadow-lg ring-2 ring-blue-400 ring-offset-2",
      isDragging && !isClone && "opacity-50",
    )}
  >
    {renderItem(message, index, dragHandleProps ?? undefined)}
  </div>
);

/**
 * Factory function that creates a `renderClone` callback for @hello-pangea/dnd's Droppable.
 *
 * ## Why is this needed?
 *
 * @hello-pangea/dnd doesn't support nested scroll containers. When a Droppable is inside
 * another scrollable element (like our Messages list), the library can't correctly
 * calculate drag positions, causing the dragged item to appear offset from the cursor.
 *
 * ## How renderClone solves this
 *
 * The `renderClone` prop tells the library to render a CLONE of the dragged item
 * at `document.body` level (outside all scroll containers). This clone:
 *
 * Meanwhile, the original item stays in the list with reduced opacity.
 *
 *
 * @param messages - Array of message stores (needed to look up dragged item by index)
 * @param renderItem - Function to render message content (same as list items)
 * @returns A renderClone callback function compatible with Droppable's renderClone prop
 *
 * @see https://github.com/hello-pangea/dnd/blob/main/docs/guides/reparenting.md
 */
export const createRenderDraggableItemClone =
  (messages: IMessageStore[], renderItem: RenderItemFn) =>
  (
    { innerRef, draggableProps, dragHandleProps }: DraggableProvided,
    { isDragging }: DraggableStateSnapshot,
    { source }: DraggableRubric,
  ) => {
    const message = messages[source.index];

    if (!message) {
      return null;
    }

    return (
      <DraggableItem
        innerRef={innerRef}
        message={message}
        index={source.index}
        draggableProps={draggableProps}
        dragHandleProps={dragHandleProps}
        renderItem={renderItem}
        isDragging={isDragging}
        isClone
      />
    );
  };
